import React, { useEffect, useRef } from 'react';
import { Alert, Box, Card, CardActionArea, Chip, CircularProgress, Stack, Tabs, Tab, Typography } from '@mui/material';
import { Image, Inventory2 } from '@mui/icons-material';

export default function DashboardCatalogContent({
  isMobile,
  selectedTab,
  setSelectedTab,
  categories,
  isLoadingCategories,
  scrollbarStyle,
  activeCategory,
  productsToShow,
  isLoadingCategoryProducts,
  categoryHasMore,
  isFetchingMoreCategoryProducts,
  loadMoreCategoryProducts,
  inStockOnly,
  setInStockOnly,
  handleAddToCart,
  disabled = false,
  disabledHint = '',
  setIsCatalogView,
  getProductStock,
  catalogFocusIndex = -1,
  setCatalogFocusIndex,
  catalogFocusZone,
  setCatalogFocusZone,
}) {
  const productRefs = useRef([]);
  const tabRefs = useRef([]);

  const handleTabChange = (e, v) => {
    setSelectedTab(v);
    setCatalogFocusIndex?.(-1);
    setCatalogFocusZone?.('tabs');
  };

  useEffect(() => {
    if (catalogFocusZone === 'products' && catalogFocusIndex >= 0) {
      productRefs.current[catalogFocusIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [catalogFocusIndex, catalogFocusZone]);

  useEffect(() => {
    tabRefs.current[selectedTab]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [selectedTab]);

  const handleProductListScroll = (e) => {
    if (!categoryHasMore || isFetchingMoreCategoryProducts) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 80) loadMoreCategoryProducts?.();
  };

  const inStockToggle = (
    <Chip
      label="In stock here"
      size="small"
      onClick={() => setInStockOnly?.(!inStockOnly)}
      sx={{
        height: 22,
        fontSize: '0.65rem',
        fontWeight: 700,
        borderRadius: '6px',
        cursor: 'pointer',
        bgcolor: inStockOnly ? '#EEF2FF' : 'transparent',
        color: inStockOnly ? '#4F46E5' : '#64748B',
        border: `1px solid ${inStockOnly ? '#C7D2FE' : '#E2E8F0'}`,
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );

  return (
    <>
      {isMobile ? (
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: '1px solid #E2E8F0',
            flexShrink: 0,
            minHeight: 40,
            '& .MuiTab-root': {
              fontSize: '0.75rem',
              minHeight: 40,
              textTransform: 'none',
              fontWeight: 600,
              py: 0.5,
            },
          }}
        >
          {categories.map((cat, idx) => <Tab key={cat.id} label={cat.displayName} ref={(el) => { tabRefs.current[idx] = el; }} />)}
        </Tabs>
      ) : null}

      {isMobile && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', py: 0.75 }}>
          {inStockToggle}
        </Box>
      )}

      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        {!isMobile && (
          <Box sx={{ ...scrollbarStyle, width: { md: 110, lg: 140 }, borderRight: '1px solid #F1F5F9', overflowY: 'auto', flexShrink: 0 }}>
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 900, color: '#94A3B8', mb: 1, letterSpacing: '0.05rem' }}>CATEGORIES</Typography>
            {isLoadingCategories ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={16} sx={{ color: '#94A3B8' }} />
              </Box>
            ) : (
              <Tabs
                orientation="vertical"
                value={selectedTab}
                onChange={handleTabChange}
                variant="scrollable"
                sx={{
                  '& .MuiTab-root': {
                    alignItems: 'flex-start', textAlign: 'left', fontSize: '0.62rem', lineHeight: 1.25,
                    fontWeight: 600, minWidth: 0, py: 0.75, textTransform: 'none',
                  },
                  '& .MuiTabs-indicator': { left: 0, width: 3, borderRadius: '0 4px 4px 0' },
                }}
              >
                {categories.map((cat, idx) => (
                  <Tab
                    key={cat.id}
                    title={cat.displayName}
                    label={
                      <Box sx={{
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                        overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {cat.displayName}
                      </Box>
                    }
                    ref={(el) => { tabRefs.current[idx] = el; }}
                  />
                ))}
              </Tabs>
            )}
          </Box>
        )}

        <Box
          onScroll={handleProductListScroll}
          sx={{ ...scrollbarStyle, flexGrow: 1, pl: { xs: 0, md: 1.5 }, overflowY: 'auto', pt: { xs: 1, md: 0 } }}
        >
          {!isMobile && (
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 900, color: '#94A3B8', letterSpacing: '0.05rem', mb: 1 }}>{activeCategory?.displayName?.toUpperCase()}</Typography>
          )}

          {disabled && disabledHint && (
            <Alert severity="warning" sx={{ mb: 1, py: 0, fontSize: '0.72rem', fontWeight: 600 }}>{disabledHint}</Alert>
          )}

          {isLoadingCategoryProducts && productsToShow.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={22} sx={{ color: '#94A3B8' }} />
            </Box>
          )}

          {!isLoadingCategoryProducts && activeCategory && productsToShow.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4, color: '#94A3B8' }}>
              <Inventory2 sx={{ fontSize: 34, mb: 1, opacity: 0.4 }} />
              <Typography fontSize="0.82rem" fontWeight={600} color="#64748B">
                {inStockOnly ? 'Nothing in stock here in this category' : 'No products in this category'}
              </Typography>
            </Box>
          )}

          <Stack spacing={1}>
            {productsToShow.map((product, idx) => {
              const isFocused = idx === catalogFocusIndex;
              return (
                <Card
                  key={product.id}
                  ref={(el) => { productRefs.current[idx] = el; }}
                  variant="outlined"
                  sx={{
                    borderRadius: '8px',
                    opacity: disabled ? 0.5 : 1,
                    '&:hover': disabled ? undefined : { borderColor: '#4F46E5', bgcolor: '#F8FAFC' },
                    bgcolor: isFocused ? '#EEF2FF' : 'transparent',
                    borderLeft: isFocused ? '3px solid #4F46E5' : '3px solid transparent',
                  }}
                >
                  <CardActionArea
                    disabled={disabled}
                    onClick={() => {
                      handleAddToCart(product);
                      if (isMobile) setIsCatalogView(false);
                    }}
                    tabIndex={0}
                    sx={{ p: 1 }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box sx={{ width: { xs: 44, md: 36 }, height: { xs: 44, md: 36 }, flexShrink: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px', overflow: 'hidden' }}>
                        {product.image_url ? <Box component="img" src={product.image_url} sx={{ height: '100%', width: '100%', objectFit: 'cover' }} /> : <Image sx={{ color: '#CBD5E1', fontSize: 20 }} />}
                      </Box>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: { xs: '0.82rem', md: '0.72rem' }, fontWeight: 700, color: isFocused ? '#4F46E5' : '#1E293B', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.name}</Typography>
                        <Typography sx={{ fontSize: { xs: '0.68rem', md: '0.62rem' }, color: '#64748B' }}>SKU: {product.sku} | Stock: {getProductStock(product)}</Typography>
                      </Box>
                      <Box sx={{ flexShrink: 0, ml: 1, textAlign: 'right' }}>
                        <Typography sx={{ fontSize: { xs: '0.9rem', md: '0.78rem' }, fontWeight: 800, color: '#4F46E5', whiteSpace: 'nowrap' }}>₹{product.retail_price}</Typography>
                        {product.price_capped_at_mrp && (
                          <Chip label="MRP" size="small" sx={{ height: 14, fontSize: '0.5rem', fontWeight: 700, bgcolor: '#FFFBEB', color: '#92400E', borderRadius: '4px', mt: 0.25, '& .MuiChip-label': { px: 0.5 } }} />
                        )}
                      </Box>
                    </Stack>
                  </CardActionArea>
                </Card>
              );
            })}
            {isFetchingMoreCategoryProducts && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={16} sx={{ color: '#94A3B8' }} />
              </Box>
            )}
          </Stack>
        </Box>
      </Box>
    </>
  );
}
